import { parseISODate } from './date';
import { mondayIsoForDateInLocal } from './dashboardDateContext';

/** 대시보드 연간 월별 달력: 학년도 2월 ~ 다음해 2월 */

export const DASHBOARD_CAL_DOW_EN = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;

export type DashboardMonthRef = {
  year: number;
  /** 1–12 */
  month: number;
};

export type DashboardMonthCell = {
  /** null = 이달 밖(회색 채움 원) */
  day: number | null;
  /** 0=일 … 6=토 */
  dow: number;
  iso: string | null;
};

function normalizeAcademicYear(academicYear: number): number {
  return Number.isFinite(academicYear) && academicYear > 0 ? Math.floor(academicYear) : new Date().getFullYear();
}

/** academicYear년 2월 1일 ~ (academicYear+1)년 2월 말 */
export function dashboardPlannerBounds(academicYear: number): { startIso: string; endIso: string } {
  const y = normalizeAcademicYear(academicYear);
  const endDay = new Date(y + 1, 2, 0).getDate();
  return {
    startIso: `${y}-02-01`,
    endIso: `${y + 1}-02-${String(endDay).padStart(2, '0')}`,
  };
}

export function plannerMinMaxMondayIso(academicYear: number): { minMonday: string; maxMonday: string } {
  const { startIso, endIso } = dashboardPlannerBounds(academicYear);
  return {
    minMonday: mondayIsoForDateInLocal(parseISODate(startIso)),
    maxMonday: mondayIsoForDateInLocal(parseISODate(endIso)),
  };
}

export function clampDateToPlannerWeeks(date: Date, academicYear: number): Date {
  const { minMonday, maxMonday } = plannerMinMaxMondayIso(academicYear);
  const mon = mondayIsoForDateInLocal(date);
  if (mon < minMonday) return parseISODate(minMonday);
  if (mon > maxMonday) return parseISODate(maxMonday);
  return date;
}

/** 학년도 달력 월(2월~다음해 2월) 안으로 해당 월 1일을 맞춘다 */
export function clampMonthToPlanner(date: Date, academicYear: number): Date {
  const { startIso, endIso } = dashboardPlannerBounds(academicYear);
  const start = parseISODate(startIso);
  const end = parseISODate(endIso);
  const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
  const minMonth = new Date(start.getFullYear(), start.getMonth(), 1);
  const maxMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  if (monthStart < minMonth) return minMonth;
  if (monthStart > maxMonth) return maxMonth;
  return monthStart;
}

/** academicYear년 2월 ~ (academicYear+1)년 2월 */
export function listDashboardYearMonths(academicYear: number): DashboardMonthRef[] {
  return listDashboardYearMonthRows(academicYear).flat();
}

/**
 * 모달 행: 2월(우정렬) / 3~6월 / 7~10월 / 11~다음해 2월
 */
export function listDashboardYearMonthRows(academicYear: number): DashboardMonthRef[][] {
  const y = normalizeAcademicYear(academicYear);
  return [
    [{ year: y, month: 2 }],
    [
      { year: y, month: 3 },
      { year: y, month: 4 },
      { year: y, month: 5 },
      { year: y, month: 6 },
    ],
    [
      { year: y, month: 7 },
      { year: y, month: 8 },
      { year: y, month: 9 },
      { year: y, month: 10 },
    ],
    [
      { year: y, month: 11 },
      { year: y, month: 12 },
      { year: y + 1, month: 1 },
      { year: y + 1, month: 2 },
    ],
  ];
}

export function buildDashboardMonthCells(year: number, month: number): DashboardMonthCell[] {
  const first = new Date(year, month - 1, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells: DashboardMonthCell[] = [];

  const toIso = (y: number, m: number, d: number): string => {
    const mm = String(m).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    return `${y}-${mm}-${dd}`;
  };

  /* 이달 앞쪽 빈 칸 = 이전달 날짜 (학기 밖 흐림 판정용) */
  const prev = new Date(year, month - 1, 0);
  const prevYear = prev.getFullYear();
  const prevMonth = prev.getMonth() + 1;
  const prevDays = prev.getDate();
  for (let i = 0; i < startDow; i += 1) {
    const d = prevDays - startDow + 1 + i;
    cells.push({ day: null, dow: i, iso: toIso(prevYear, prevMonth, d) });
  }

  for (let d = 1; d <= daysInMonth; d += 1) {
    const dow = (startDow + d - 1) % 7;
    cells.push({ day: d, dow, iso: toIso(year, month, d) });
  }

  /* 이달 뒤쪽 빈 칸 = 다음달 날짜 */
  let nextY = year;
  let nextM = month + 1;
  if (nextM > 12) {
    nextM = 1;
    nextY += 1;
  }
  let nextDay = 1;
  while (cells.length % 7 !== 0 || cells.length < 42) {
    cells.push({
      day: null,
      dow: cells.length % 7,
      iso: toIso(nextY, nextM, nextDay),
    });
    nextDay += 1;
  }
  return cells;
}

export function formatDashboardMonthDigit(month: number): string {
  const m = Math.min(12, Math.max(1, Math.floor(month)));
  return String(m);
}
